import { useCallback, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import {
  EuiButton,
  EuiButtonEmpty,
  EuiErrorBoundary,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiOverlayMask,
  EuiSpacer,
  EuiText,
} from '@elastic/eui';
import { Toast } from '@elastic/eui/src/components/toast/global_toast_list';
import { useGetDevicesByOrganizationLazyQuery, useGetGroupDevicesLazyQuery } from 'gqlHooks';
import { fetchAllOrganizationDevices } from 'utils/fetchAllOrganizationDevices';
import { fetchAllGroupDevices } from 'utils/fetchAllGroupDevices';
import {
  buildDeviceListExportData,
  getDeviceExportLimitMessage,
  getDeviceListExportDescription,
  getDeviceListExportFileName,
  getGroupDeviceListExportFileName,
} from 'utils/device-list-export';
import { errorToastMessage } from 'app/utils/toast-messages';
import { CONSTANTS } from 'utils/constants';

type DeviceListExportProps = {
  organizationId: string; // org ID for API calls
  organizationName?: string; // org display name for filename + modal copy
  searchTerm?: string;
  groupToken?: string; // selectedGroup.identifier — when set, export group devices
  groupName?: string; // selectedGroup.label — display name for filename + modal copy
  isDisabled?: boolean;
  addToastMessage: (message: Toast) => void;
};

// export CSV button + download modal for the device list (org or group scope)
export const DeviceListExport = ({
  organizationId,
  organizationName,
  searchTerm,
  groupToken,
  groupName,
  isDisabled = false,
  addToastMessage,
}: DeviceListExportProps) => {
  const isGroupExport = Boolean(groupToken);

  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [exportCsvData, setExportCsvData] = useState<string | string[][]>('');
  const [exportFileName, setExportFileName] = useState('');
  const [exportDescription, setExportDescription] = useState('');

  const [fetchOrgPage] = useGetDevicesByOrganizationLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });

  const [fetchGroupPage] = useGetGroupDevicesLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
  });

  const isExportingRef = useRef(false);

  const handleDownloadModalClose = useCallback(() => {
    setIsDownloadModalOpen(false);
    setExportCsvData('');
    setExportFileName('');
    setExportDescription('');
  }, []);

  const handleExportClick = useCallback(async () => {
    if (isDisabled || isExportingRef.current) {
      return;
    }

    isExportingRef.current = true;
    setIsExporting(true);

    try {
      const { devices, truncated, error } = isGroupExport
        ? await fetchAllGroupDevices({
            organizationId,
            groupToken: groupToken!,
            searchTerm,
            recursive: false,
            fetchPage: fetchGroupPage,
            pageSize: CONSTANTS.EXPORT_DEVICE_PAGE_SIZE,
            maxPages: CONSTANTS.EXPORT_DEVICE_MAX_PAGES,
          })
        : await fetchAllOrganizationDevices({
            organizationId,
            searchTerm,
            fetchPage: fetchOrgPage,
            pageSize: CONSTANTS.EXPORT_DEVICE_PAGE_SIZE,
            maxPages: CONSTANTS.EXPORT_DEVICE_MAX_PAGES,
          });

      if (error) {
        addToastMessage(errorToastMessage('Failed to fetch devices for export'));
        return;
      }
      if (truncated) {
        addToastMessage(errorToastMessage(getDeviceExportLimitMessage()));
        return;
      }
      if (!devices.length) {
        addToastMessage(errorToastMessage('No devices found for export'));
        return;
      }

      const csvData = buildDeviceListExportData(devices);
      if (!csvData || csvData === '') {
        addToastMessage(errorToastMessage('No devices to export'));
        return;
      }

      const fileName = isGroupExport
        ? getGroupDeviceListExportFileName({ organizationName, groupName, searchTerm })
        : getDeviceListExportFileName({ organizationName, searchTerm });

      const description = getDeviceListExportDescription({
        organizationName,
        groupName: isGroupExport ? groupName : undefined,
        searchTerm,
      });

      setExportCsvData(csvData);
      setExportFileName(fileName);
      setExportDescription(description);
      setIsDownloadModalOpen(true);
    } finally {
      isExportingRef.current = false;
      setIsExporting(false);
    }
  }, [
    addToastMessage,
    fetchGroupPage,
    fetchOrgPage,
    groupName,
    groupToken,
    isDisabled,
    isGroupExport,
    organizationId,
    organizationName,
    searchTerm,
  ]);

  const downloadModal = isDownloadModalOpen ? (
    <EuiOverlayMask>
      <EuiModal
        initialFocus="[name=closeButton]"
        onClose={handleDownloadModalClose}
      >
        <EuiModalHeader>
          <EuiModalHeaderTitle>Generated Export</EuiModalHeaderTitle>
        </EuiModalHeader>
        <EuiModalBody>
          <EuiText size="s">
            <p style={{ whiteSpace: 'pre-line' }}>{exportDescription}</p>
          </EuiText>
          <EuiSpacer size="xl" />
          <CSVLink
            data={exportCsvData}
            filename={exportFileName}
          >
            Download CSV
          </CSVLink>
        </EuiModalBody>
        <EuiModalFooter>
          <EuiButtonEmpty
            id="close-device-list-export-modal"
            onClick={handleDownloadModalClose}
          >
            Close
          </EuiButtonEmpty>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  ) : null;

  return (
    <EuiErrorBoundary>
      {downloadModal}
      <EuiButton
        id={isGroupExport ? 'export-group-device-list' : 'export-org-device-list'}
        color="text"
        isDisabled={isDisabled || isExporting}
        isLoading={isExporting}
        onClick={handleExportClick}
      >
        {isGroupExport ? 'Download Group' : 'Download Org'}
      </EuiButton>
    </EuiErrorBoundary>
  );
};

export default DeviceListExport;
